// Hash-based navigation for PDP page
// Supported hashes:
//   #Map - switch to nearby tab and scroll to map
//   #property, #nearby - scroll to information tab
//   #2DK - scroll to room card
//   #3K-25-308 - scroll to room item
//   #Unit-05-0404 - scroll to room by building-room
//   #Unit-01-0505-Maisoku - expand room and open maisoku popup
//   #Unit-01-0505-CostCalc - expand room and open initial cost popup
//   #Unit-01-0505-NormalInquiry - expand room and open enquiry form popup
//   #Unit-01-0505-SiteVisit - expand room and open book visit popup
//   #Unit-01-0505-Gallery - expand room and open gallery popup
//   #NormalInquiry, #SiteVisit, #Waitlist - open contact bottom popups
export function setupUrlHash() {
  const informationCtr = document.querySelector(".container-information");
  const roomsCtr = document.querySelector(".container-rooms");
  const contactBottomCtr = document.querySelector(".container-contact-bottom");

  if (informationCtr) setupInformation();
  if (roomsCtr) setupRooms();

  const handleHash = () => {
    if (informationCtr) handleInformationHash();
    if (roomsCtr) handleRoomsHash();
    if (contactBottomCtr) handleContactBottomHash();
  };

  handleHash();
  window.addEventListener("hashchange", handleHash);

  // handle same-page hash link clicks when hash is already the same
  document.addEventListener("click", (e) => {
    const link = (e.target as HTMLElement)?.closest<HTMLAnchorElement>(
      "a[href*='#']"
    );
    if (!link) return;

    const linkUrl = new URL(link.href, window.location.origin);
    const currentUrl = new URL(window.location.href);

    if (
      linkUrl.origin === currentUrl.origin &&
      linkUrl.pathname === currentUrl.pathname &&
      linkUrl.hash === currentUrl.hash &&
      linkUrl.hash !== ""
    ) {
      e.preventDefault();
      handleHash();
    }
  });

  function setupInformation() {
    // [data-anchor-tab] click handler
    document.querySelectorAll("[data-anchor-tab]").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const name = link.getAttribute("data-anchor-tab");
        if (name) {
          const targetHash = "#" + name;
          if (decodeURIComponent(window.location.hash) === targetHash) {
            handleInformationHash();
            return;
          }
          window.location.hash = targetHash;
        }
      });
    });
  }

  function handleInformationHash() {
    const hash = decodeURIComponent(window.location.hash).replace("#", "");
    if (hash) {
      // #Map - switch to nearby tab and scroll to map
      if (hash.toLowerCase() === "map") {
        const nearbyTab = informationCtr!.querySelector<HTMLElement>(
          '.container-information-tab[data-name="nearby"]'
        );
        nearbyTab?.click();
        const mapFilter = document.querySelector<HTMLElement>(
          ".container-map-filter"
        );
        if (mapFilter) {
          window.scrollTo({ top: mapFilter.offsetTop, behavior: "smooth" });
        }
      } else {
        // #property, #nearby - scroll to information tab
        scrollToInformationTab(hash);
      }
    }
  }

  function scrollToInformationTab(name: string) {
    const tab = informationCtr!.querySelector<HTMLElement>(
      `.container-information-tab[data-name="${name}"]`
    );
    if (tab) {
      tab.click();
      window.scrollTo({ top: tab.offsetTop, behavior: "smooth" });
    }
  }

  function setupRooms() {
    // [data-anchor-room] click handler
    document.querySelectorAll("[data-anchor-room]").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const hash = link.getAttribute("data-anchor-room");
        if (hash) {
          const targetHash = "#" + hash;
          if (decodeURIComponent(window.location.hash) === targetHash) {
            handleRoomsHash();
            return;
          }
          window.location.hash = targetHash;
        }
      });
    });

    // [data-scroll-to-name] click handler in gallery
    document.addEventListener("click", (e) => {
      const button = (e.target as HTMLElement)?.closest<HTMLElement>(
        ".container-gallery-section-button[data-scroll-to-name]"
      );
      const name = button?.getAttribute("data-scroll-to-name");
      if (name) {
        const card = roomsCtr!.querySelector<HTMLElement>(
          `.container-rooms-group-card[data-name="${name}"]`
        );
        if (card) {
          closeAllCard();
          card.removeAttribute("data-closed");
          window.scrollTo({ top: card.offsetTop, behavior: "smooth" });
        }
      }
    });
  }

  function handleRoomsHash() {
    const hash = decodeURIComponent(window.location.hash);
    if (!hash) return;

    // #Unit-{building}-{room}[-{action}] - scroll to room and optionally trigger action
    const unitInfo = parseUnitHash(hash);
    if (unitInfo) {
      scrollToRoomItem(unitInfo.dataRoom, unitInfo.action);
    } else {
      const parts = hash.replace("#", "").split("-");
      if (parts.length >= 2) {
        // #3K-25-308 - scroll to room item
        scrollToRoomItem(parts.slice(1).join("-"));
      } else {
        // #2DK - scroll to room card
        scrollToCard(parts[0]);
      }
    }
  }

  function parseUnitHash(hash: string) {
    // #Unit-01-0505-Gallery/  => a slash at the end are also supported
    const match = hash
      .replace(/\/+$/, "")
      .match(/^#?Unit-(\d+)-(\d+)(?:-(\w+))?$/i);
    if (!match) return null;
    return {
      dataRoom: `${parseInt(match[1], 10)}-${parseInt(match[2], 10)}`,
      action: match[3]?.toLowerCase() || null,
    };
  }

  function scrollToCard(name: string) {
    const card = roomsCtr!.querySelector<HTMLElement>(
      `.container-rooms-group-card[data-name="${name}"]`
    );
    if (card) {
      closeAllCard();
      card.removeAttribute("data-closed");
      window.scrollTo({ top: card.offsetTop, behavior: "smooth" });
    }
  }

  function scrollToRoomItem(dataRoom: string, action?: string | null) {
    const roomItem = roomsCtr!.querySelector<HTMLElement>(
      `.container-rooms-group-card-list-item[data-room="${dataRoom}"]`
    );
    if (!roomItem) return;

    const parentCard = roomItem.closest(".container-rooms-group-card");
    if (parentCard) {
      closeAllCard();
      parentCard.removeAttribute("data-closed");
    }

    window.scrollTo({ top: roomItem.offsetTop, behavior: "smooth" });

    if (action) {
      const popupIdMap: Record<string, string> = {
        maisoku: "room-maisoku",
        costcalc: "initial-cost",
        normalinquiry: "enquiry-form",
        sitevisit: "book-visit",
        gallery: "rooms-gallery",
      };
      const popupId = popupIdMap[action];
      if (popupId) {
        roomItem
          .querySelector<HTMLElement>(
            ".container-rooms-group-card-list-item-collapse"
          )
          ?.click();
        roomItem
          .querySelector<HTMLElement>(
            `[data-popup-id="${popupId}"][data-popup-event="popup-open"]`
          )
          ?.click();
      }
    }
  }

  function closeAllCard() {
    roomsCtr!
      .querySelectorAll(".container-rooms-group-card")
      .forEach((card) => {
        card.setAttribute("data-closed", "");
      });
  }

  // #NormalInquiry, #SiteVisit, #Waitlist - open contact bottom popups
  function handleContactBottomHash() {
    const hash = decodeURIComponent(window.location.hash)
      .replace("#", "")
      .toLowerCase();
    const popupIdMap: Record<string, string> = {
      normalinquiry: "enquiry-form",
      sitevisit: "book-visit",
      waitlist: "enquiry-form",
    };
    const popupId = popupIdMap[hash];
    if (popupId) {
      contactBottomCtr!
        .querySelector<HTMLElement>(
          `[data-popup-id="${popupId}"][data-popup-event="popup-open"]`
        )
        ?.click();
    }
  }
}
